/* eslint-disable no-empty */
import * as Yup from 'yup'
import type { SourcesResponse } from '../app/interfaces'
import { removeLeadingSlash } from './urlHelpers'

interface SourceMetaData {
  sourceDescription: string
  sourceName: string
}

export const originFormInitialValues: SourcesResponse = {
  // generic
  id: '',
  name: '',
  typeId: '1',
  includeCanonicalHeader: false,
  baseUrlForCanonicalHeader: '',
  // s3
  s3Bucket: '',
  s3Prefix: '',
  s3AccessKey: '',
  s3SecretKey: '',
  endpoint: '',
  s3ForcePathStyle: false,
  // google
  bucketName: '',
  clientEmail: '',
  googlePrefix: '',
  privateKey: '',
  // azure
  containerName: '',
  accountName: '',
  azurePrefix: '',
  sasToken: '',
  // web folder
  baseUrl: '',
  forwardHostHeaderToOrigin: false,
  associatedPatterns: [],
  associatedUrlEndpoints: [],
  //akeneo
  clientId: '',
  secret: '',
  username: '',
  password: ''
}

export const SourcesSchema = Yup.object().shape({
  // generic field validations
  name: Yup.string()
    .min(1, 'Minimum length should be 1')
    .required('Required')
    .matches(
      /^[a-zA-Z0-9 ]+$/u,
      'Origin Name can only have alphanumeric characters',
    ),
  // only '1', '2', '3', '5', '6' go to backend
  typeId: Yup.mixed()
    .oneOf(['1', '2', '3', '5', '6', '7', '8', '9'], 'Invalid Origin Type')
    .required('Required'),
  includeCanonicalHeader: Yup.boolean().required('Required'),
  baseUrlForCanonicalHeader: Yup.string().when('includeCanonicalHeader', {
    is: true,
    then: Yup.string()
      .required('Required')
      .min(1, 'Minimum length should be 1')
      .matches(
        // eslint-disable-next-line max-len
        /^$|^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()](?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)$/u,
        'Not a valid URL. A valid URL looks like http://www.google.com',
      ),
  }),
  // s3 schema
  s3Bucket: Yup.string()
    .min(1, 'Minimum length should be 1')
    .when('typeId', {
      is: (value: string) => value === '1' || value === '3' || value === '7',
      then: Yup.string().required('Required'),
    }),
  s3AccessKey: Yup.string()
    .min(1, 'Minimum length should be 1')
    .when('typeId', {
      is: (value: string) => value === '1' || value === '3' || value === '7',
      then: Yup.string().required('Required'),
    }),
  s3Prefix: Yup.string()
    .min(1, 'Minimum length should be 1')
    .when('typeId', {
      is: (value: string) => value === '1' || value === '3' || value === '7',
      then: Yup.string().required('Required'),
    }),
  s3SecretKey: Yup.string()
    .min(1, 'Minimum length should be 1')
    .when('typeId', {
      is: (value: string) => value === '1' || value === '3' || value === '7',
      then: Yup.string().required('Required'),
    }),
  endpoint: Yup.string()
    .min(1, 'Minimum length should be 1')
    .matches(
      // eslint-disable-next-line max-len
      /^$|^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()](?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)$/u,
      'Not a valid URL. A valid URL looks like http://www.google.com',
    )
    .when('typeId', {
      is: (value: string) => value === '7',
      then: Yup.string().required('Required'),
    }),
  // web folder schema
  baseUrl: Yup.string()
    .min(1, 'Minimum length should be 1')
    .matches(
      // eslint-disable-next-line max-len
      /^$|^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()](?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)$/u,
      'Not a valid URL. A valid URL looks like http://www.google.com',
    )
    .when('typeId', {
      is: (value: string) => value === '2' || value === '9',
      then: Yup.string().required('Required'),
    }),
  forwardHostHeaderToOrigin: Yup.string()
    .min(1, 'Minimum length should be 1')
    .when('typeId', {
      is: (value: string) => value === '2',
      then: Yup.string().required('Required'),
    }),
  // google schema
  bucketName: Yup.string()
    .min(1, 'Minimum length should be 1')
    .when('typeId', {
      is: (value: string) => value === '5',
      then: Yup.string().required('Required'),
    }),
  clientEmail: Yup.string()
    .email('Must be a valid email')
    .when('typeId', {
      is: (value: string) => value === '5',
      then: Yup.string().required('Required'),
    }),
  googlePrefix: Yup.string()
    .min(1, 'Minimum length should be 1')
    .when('typeId', {
      is: (value: string) => value === '5',
      then: Yup.string().required('Required'),
    }),
  privateKey: Yup.string()
    .min(1, 'Minimum length should be 1')
    .when('typeId', {
      is: (value: string) => value === '5',
      then: Yup.string().required('Required'),
    }),
  // azure schema
  containerName: Yup.string()
    .min(1, 'Minimum length should be 1')
    .when('typeId', {
      is: (value: string) => value === '6',
      then: Yup.string().required('Required'),
    }),
  accountName: Yup.string()
    .min(1, 'Minimum length should be 1')
    .when('typeId', {
      is: (value: string) => value === '6',
      then: Yup.string().required('Required'),
    }),
  azurePrefix: Yup.string()
    .min(1, 'Minimum length should be 1')
    .when('typeId', {
      is: (value: string) => value === '6',
      then: Yup.string().required('Required'),
    }),
  sasToken: Yup.string()
    .min(1, 'Minimum length should be 1')
    .when('typeId', {
      is: (value: string) => value === '6',
      then: Yup.string().required('Required'),
    }),
  //akeneo schema, base URL is covered in web folder
  clientId: Yup.string()
    .min(1, 'Minimum length should be 1')
    .when('typeId', {
      is: (value: string) => value === '9',
      then: Yup.string().required('Required'),
    }),
  secret: Yup.string()
    .min(1, 'Minimum length should be 1')
    .when('typeId', {
      is: (value: string) => value === '9',
      then: Yup.string().required('Required'),
    }),
  username: Yup.string()
    .min(1, 'Minimum length should be 1')
    .when('typeId', {
      is: (value: string) => value === '9',
      then: Yup.string().required('Required'),
    }),
  password: Yup.string()
    .min(1, 'Minimum length should be 1')
    .when('typeId', {
      is: (value: string) => value === '9',
      then: Yup.string().required('Required'),
    }),
})

export const sourceTypeEnum = {
  AMAZON_S3: 1,
  WEB_FOLDER: 2,
  CLOUDINARY_BACKUP: 3,
  GOOGLE_CLOUD_STORAGE: 5,
  AZURE: 6,
  S3_COMPATIBLE_STORAGE: 7, // Only for react frontend - does not go to backend
  WEB_PROXY: 8, // Only for react frontend - does not go to backend
  AKENEO_PIM: 9
}

export const sourceTextMapping = {
  [sourceTypeEnum.AMAZON_S3]: 'Amazon S3',
  [sourceTypeEnum.CLOUDINARY_BACKUP]: "Cloudinary's Backup Bucket",
  [sourceTypeEnum.S3_COMPATIBLE_STORAGE]:
    'S3-Compatible Storage (Wasabi, Digital Ocean Spaces, Ali Storage, etc.)',
  [sourceTypeEnum.GOOGLE_CLOUD_STORAGE]: 'Google Cloud Storage',
  [sourceTypeEnum.AZURE]: 'Azure Storage',
  [sourceTypeEnum.WEB_FOLDER]:
    'Web Folder - HTTP(S) server and Magento, Shopify, Wordpress, etc.',
  [sourceTypeEnum.WEB_PROXY]: 'Web Proxy',
  [sourceTypeEnum.AKENEO_PIM]: 'Akeneo PIM'
}

export const sourceTypeMapping = {
  [sourceTypeEnum.AMAZON_S3]: {
    name: 'Amazon S3',
    description: '',
  },
  [sourceTypeEnum.CLOUDINARY_BACKUP]: {
    name: "Cloudinary's Backup Bucket",
    description: '',
  },
  [sourceTypeEnum.S3_COMPATIBLE_STORAGE]: {
    name: 'S3-Compatible Storage',
    description: '(Wasabi, Digital Ocean Spaces, Ali Storage, etc.)',
  },
  [sourceTypeEnum.GOOGLE_CLOUD_STORAGE]: {
    name: 'Google Cloud Storage',
    description: '',
  },
  [sourceTypeEnum.AZURE]: {
    name: 'Azure Storage',
    description: '',
  },
  [sourceTypeEnum.WEB_FOLDER]: {
    name: 'Web Folder',
    description: 'HTTP(S) server and Magento, Shopify, Wordpress, etc.',
  },
  [sourceTypeEnum.WEB_PROXY]: {
    name: 'Web Proxy',
    description: '',
  },
  [sourceTypeEnum.AKENEO_PIM]: {
    name: 'Akeneo PIM',
    description: '',
  }
}

export const getOriginIconUrl = (source: {
  isDefault?: boolean
  typeId: number | string
  baseUrl?: string
}): string => {
  if (source.isDefault) {
    return 'https://ik.imagekit.io/demo/favicon/imagekit.io'
  }
  if (source.typeId === 1 || source.typeId === 7) {
    return 'https://ik.imagekit.io/ikmedia/website-assets/platform-icons/amazon-s3_zkL6BY_1T.svg'
  }
  if (source.typeId === 2) {
    let faviconDomain = ''
    try {
      faviconDomain = new URL(source.baseUrl ?? '').hostname
        .split('.')
        .slice(-2)
        .join('.')
    } catch {}
    if (faviconDomain) {
      return `https://ik.imagekit.io/demo/favicon/${faviconDomain}`
    }
    return 'https://ik.imagekit.io/demo/img/icons8-server-64_sibZOIGTb.png'
  }
  if (source.typeId === 3) {
    return 'https://ik.imagekit.io/ikmedia/website-assets/platform-icons/amazon-s3_zkL6BY_1T.svg'
  }
  if (source.typeId === 5) {
    return 'https://ik.imagekit.io/ikmedia/website-assets/platform-icons/cloud-storage_b624_4URj.svg'
  }
  if (source.typeId === 6) {
    return 'https://ik.imagekit.io/ikmedia/website-assets/platform-icons/azure-icon_w5jvS57ntq.svg'
  }
  if (source.typeId === 9) {
    return 'https://ik.imagekit.io/ikmedia/website-assets/platform-icons/akeneo_XP1QJm37E.png?tr=w-128'
  }
  return 'https://ik.imagekit.io/demo/img/icons8-server-64_sibZOIGTb.png'
}

export const extractMetadata = (source: SourcesResponse): SourceMetaData => {
  const { typeId: sourceType } = source
  let sourceDescription = ''
  let sourceName = source.name

  if (source.isDefault) {
    sourceDescription = 'Media library'
    sourceName = 'ImageKit Media Library'
  } else if (
    sourceType === sourceTypeEnum.AMAZON_S3 ||
    sourceType === sourceTypeEnum.S3_COMPATIBLE_STORAGE
  ) {
    const { s3Bucket, s3Prefix } = source
    // sourceDescription = `Your Amazon S3 bucket: ${s3Bucket}(/${s3Prefix})`;
    sourceDescription = `${s3Bucket}(/${removeLeadingSlash(s3Prefix ?? '')})`
  } else if (sourceType === sourceTypeEnum.CLOUDINARY_BACKUP) {
    const { s3Bucket, s3Prefix } = source
    // sourceDescription = `Your Cloudinary Backup bucket bucket: ${s3Bucket}(/${s3Prefix})`;
    sourceDescription = `${s3Bucket}(/${removeLeadingSlash(s3Prefix ?? '')})`
  } else if (sourceType === sourceTypeEnum.GOOGLE_CLOUD_STORAGE) {
    const { bucketName, googlePrefix } = source
    // sourceDescription = `Your Google bucket: ${bucketName}(/${googlePrefix})`;
    sourceDescription = `${bucketName}(/${removeLeadingSlash(
      googlePrefix ?? '',
    )})`
  } else if (sourceType === sourceTypeEnum.AZURE) {
    const { containerName, azurePrefix } = source
    // sourceDescription = `Your Azure Container: ${containerName}(/${azurePrefix})`;
    sourceDescription = `${containerName}(/${removeLeadingSlash(
      azurePrefix ?? '',
    )})`
  } else if (
    sourceType === sourceTypeEnum.WEB_FOLDER ||
    sourceType === sourceTypeEnum.WEB_PROXY
  ) {
    const { baseUrl } = source
    // sourceDescription = `Your Web Folder at ${baseUrl}`;
    sourceDescription = `${baseUrl ?? 'Web Proxy'}`
  } else if (sourceType === sourceTypeEnum.AKENEO_PIM) {
    const { baseUrl } = source
    sourceDescription = `${baseUrl}`; 
  }

  return {
    sourceDescription,
    sourceName,
  }
}
